import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, Alert, Typography, Space, Button, message, DatePicker, Switch, Tooltip } from "antd";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../../components/AgGridWithColumnState";
import dayjs from "dayjs";
import { SwapOutlined } from "@ant-design/icons";
import {
  getVendorAgingReport,
  exportVendorAgingReport,
} from "../../api/accounting";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import ExcelExportModal from "../Shared/Modals/ExcelExportModal";
import useGlobalFilters from "../../../../hooks/useGlobalFilters";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import { formatCurrencyWithConversion, getGlobalDisplayCurrency } from "../../utils/currencyUtils";
import {
  getCachedData,
  setCachedData,
  CacheTTL,
} from "../../context/AccountingDataCache";

const VendorAgingReport = () => {
  // Global currency display - refreshKey changes when currency changes, forcing grid refresh
  const { refreshKey: currencyRefreshKey, displayCurrency, exchangeRate, isConverted, decimalPrecision } = useDisplayCurrency();
  const [loading, setLoading] = useState(false);
  const [agingData, setAgingData] = useState({
    vendors: [],
    bucket_totals: {},
    grand_total: 0,
  });
  const [error, setError] = useState(null);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [hideZeroTotals, setHideZeroTotals] = useState(false);
  
  // Single date filter - aging buckets will be calculated relative to this date
  const [referenceDate, setReferenceDate] = useState(dayjs()); // Default to today

  const { brandIds } = useGlobalFilters();
  
  // Grid API ref for accessing filtered data
  const gridApiRef = useRef(null);
  
  // Filter and sort persistence refs
  const savedFilterStateRef = useRef(null);
  const savedSortStateRef = useRef([{ colId: "vendor_name", sort: "asc" }]);
  const pendingFilterRestoreRef = useRef(false);
  const pendingSortRestoreRef = useRef(false);

  // Memoize brandIds as a string to prevent infinite loop
  const brandIdsKey = useMemo(() => brandIds.join(","), [brandIds]);
  
  // Create stable date key for dependency tracking
  const referenceDateKey = useMemo(() => {
    return referenceDate ? referenceDate.format("YYYY-MM-DD") : "";
  }, [referenceDate]);

  const lastFetchedCacheKeyRef = useRef(null);

  // Generate cache key based on all parameters that affect the data
  const cacheKey = useMemo(() => {
    if (!brandIdsKey) return null;
    return `vendor_aging_report_${brandIdsKey}_${referenceDateKey}_${hideZeroTotals}`;
  }, [brandIdsKey, referenceDateKey, hideZeroTotals]);

  const fetchAgingReport = useCallback(async (force = false) => {
    if (brandIds.length === 0 || !cacheKey) {
      setError("Please select a brand from the header");
      setAgingData({
        vendors: [],
        bucket_totals: {},
        grand_total: 0,
      });
      return;
    }

    // Check GLOBAL cache first if not forcing refresh
    if (!force) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData && lastFetchedCacheKeyRef.current === cacheKey) {
        // Data already loaded for this key
        return;
      }
      if (cachedData) {
        setAgingData(cachedData);
        lastFetchedCacheKeyRef.current = cacheKey;
        return;
      }
    }

    // Save current filter and sort state BEFORE refreshing
    if (gridApiRef.current) {
      try {
        const currentFilters = gridApiRef.current.getFilterModel();
        if (currentFilters && Object.keys(currentFilters).length > 0) {
          savedFilterStateRef.current = currentFilters;
          pendingFilterRestoreRef.current = true;
        }
        const columnState = gridApiRef.current.getColumnState();
        const sortState = columnState
          .filter((col) => col.sort !== null)
          .map((col) => ({ colId: col.colId, sort: col.sort, sortIndex: col.sortIndex }));
        if (sortState.length > 0) {
          savedSortStateRef.current = sortState;
          pendingSortRestoreRef.current = true;
        }
      } catch (e) {
        // Grid might not be ready, ignore
      }
    }

    setLoading(true);
    setError(null);

    try {
      const apiFilters = {};
      
      // Use the selected reference date for calculating aging buckets
      // The backend will calculate aging buckets relative to this date
      if (referenceDate) {
        apiFilters.upcoming_start_date = referenceDate.format("YYYY-MM-DD"); // Reference date for aging calculation
        apiFilters.upcoming_end_date = referenceDate.clone().add(90, "days").format("YYYY-MM-DD"); // Show upcoming 90 days from reference date
      }
      
      const data = await getVendorAgingReport(brandIds, apiFilters);
      setAgingData(data);
      // Store in GLOBAL cache (5 minutes TTL)
      setCachedData(cacheKey, data, CacheTTL.MEDIUM);
      lastFetchedCacheKeyRef.current = cacheKey;
    } catch (err) {
      console.error("Error fetching vendor aging report:", err);
      setError("Failed to load vendor aging report. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [brandIds, referenceDateKey, cacheKey]); // Use stable key instead of date object

  const formatCurrency = (value, currency = "$") => {
    // Use currency conversion utility - show swap icon when converted
    return formatCurrencyWithConversion(value, currency, true);
  };

  // Format number with commas for display (e.g., grand total)
  const formatNumberWithCommas = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('en-US', { 
      minimumFractionDigits: decimalPrecision, 
      maximumFractionDigits: decimalPrecision 
    });
  };

  const getValueColor = (value) => (value < 0 ? "green" : "inherit");

  // Filter vendors based on hideZeroTotals toggle
  const filteredVendors = useMemo(() => {
    if (!hideZeroTotals) {
      return agingData.vendors || [];
    }
    return (agingData.vendors || []).filter((vendor) => {
      const total = parseFloat(vendor.total) || 0;
      return total !== 0;
    });
  }, [agingData.vendors, hideZeroTotals]);

  // Calculate grand total from filtered vendors
  const filteredGrandTotal = useMemo(() => {
    return filteredVendors.reduce((sum, vendor) => {
      return sum + (parseFloat(vendor.total) || 0);
    }, 0);
  }, [filteredVendors]);

  // AG Grid column definitions
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Vendor",
        field: "vendor_name",
        pinned: "left",
        sortable: true,
        filter: true,
        width: 200,
        resizable: true,
      },
      {
        headerName: "Overpayment",
        field: "overpayment_amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        resizable: true,
        cellRenderer: (params) => {
          const currency = params.data?.currency || "$";
          return formatCurrency(params.value || 0, currency);
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Upcoming 90-60 Days",
        field: "upcoming_90_60",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 180,
        resizable: true,
        cellRenderer: (params) => {
          const currency = params.data?.currency || "$";
          return formatCurrency(params.value || 0, currency);
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Upcoming 60-30 Days",
        field: "upcoming_60_30",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 180,
        resizable: true,
        cellRenderer: (params) => {
          const currency = params.data?.currency || "$";
          return formatCurrency(params.value || 0, currency);
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Upcoming 30-1 Days",
        field: "upcoming_30_1",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 170,
        resizable: true,
        cellRenderer: (params) => {
          const currency = params.data?.currency || "$";
          return formatCurrency(params.value || 0, currency);
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Current",
        field: "current",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 140,
        resizable: true,
        cellRenderer: (params) => {
          const currency = params.data?.currency || "$";
          return formatCurrency(params.value || 0, currency);
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Past 1-30 Days",
        field: "past_1_30",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        resizable: true,
        cellRenderer: (params) => {
          const currency = params.data?.currency || "$";
          return formatCurrency(params.value || 0, currency);
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Past 30-60 Days",
        field: "past_30_60",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 160,
        resizable: true,
        cellRenderer: (params) => {
          const currency = params.data?.currency || "$";
          return formatCurrency(params.value || 0, currency);
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Past 60-90 Days",
        field: "past_60_90",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 160,
        resizable: true,
        cellRenderer: (params) => {
          const currency = params.data?.currency || "$";
          return formatCurrency(params.value || 0, currency);
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Past 90+ Days",
        field: "past_90_plus",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        resizable: true,
        cellRenderer: (params) => {
          const currency = params.data?.currency || "$";
          return formatCurrency(params.value || 0, currency);
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Total",
        field: "total",
        pinned: "right",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        resizable: true,
        cellRenderer: (params) => {
          const currency = params.data?.currency || "$";
          return formatCurrency(params.value || 0, currency);
        },
        cellStyle: (params) => ({
          textAlign: "right",
          fontWeight: "bold",
          color: getValueColor(params.value),
        }),
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
    }),
    []
  );

  const pinnedBottomRowData = useMemo(() => {
    if (!filteredVendors || filteredVendors.length === 0) return [];
    const fields = [
      "overpayment_amount", "upcoming_90_60", "upcoming_60_30", "upcoming_30_1",
      "current", "past_1_30", "past_30_60", "past_60_90", "past_90_plus", "total",
    ];
    const totals = { vendor_name: "TOTAL" };
    fields.forEach((f) => {
      totals[f] = filteredVendors.reduce(
        (sum, v) => sum + (parseFloat(v[f]) || 0),
        0
      );
    });
    return [totals];
  }, [filteredVendors]);

  const handleExportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExportModalVisible(true);
  };

  const handleExport = async (returnFile = false) => {
    if (brandIds.length === 0) {
      throw new Error("Please select a brand from the header");
    }

    setExportLoading(true);
    try {
      const apiFilters = {};
      
      // Use the selected reference date for calculating aging buckets
      if (referenceDate) {
        apiFilters.upcoming_start_date = referenceDate.format("YYYY-MM-DD"); // Reference date for aging calculation
        apiFilters.upcoming_end_date = referenceDate.clone().add(90, "days").format("YYYY-MM-DD"); // Show upcoming 90 days from reference date
      }
      
      const response = await exportVendorAgingReport(brandIds, apiFilters);

      if (returnFile) {
        return response.data;
      } else {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "vendor_aging_report.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Export error:", error);
      const backendMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        "Export failed";
      message.error(backendMsg);
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    fetchAgingReport();
  }, [fetchAgingReport]);

  // Effect to restore filters and sort AFTER data has loaded
  useEffect(() => {
    const hasFiltersToRestore = pendingFilterRestoreRef.current &&
      savedFilterStateRef.current &&
      Object.keys(savedFilterStateRef.current).length > 0;
    const hasSortToRestore = pendingSortRestoreRef.current &&
      savedSortStateRef.current &&
      savedSortStateRef.current.length > 0;

    if ((hasFiltersToRestore || hasSortToRestore) && !loading && filteredVendors.length > 0) {
      const restoreGridState = (attempt = 1) => {
        setTimeout(() => {
          if (gridApiRef.current) {
            try {
              if (hasFiltersToRestore) {
                gridApiRef.current.setFilterModel(savedFilterStateRef.current);
                pendingFilterRestoreRef.current = false;
              }
              if (hasSortToRestore) {
                gridApiRef.current.applyColumnState({
                  state: savedSortStateRef.current,
                  defaultState: { sort: null }
                });
                pendingSortRestoreRef.current = false;
              }
            } catch (e) {
              if (attempt < 5) restoreGridState(attempt + 1);
              else {
                pendingFilterRestoreRef.current = false;
                pendingSortRestoreRef.current = false;
              }
            }
          } else if (attempt < 5) {
            restoreGridState(attempt + 1);
          } else {
            pendingFilterRestoreRef.current = false;
            pendingSortRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreGridState();
    }
  }, [loading, filteredVendors.length]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Vendor Invoice Aging Report</h4>
            </div>
          </div>
          <div className="page-btn">
            <Space>
              <Space>
                <span style={{ fontSize: "14px" }}>Hide $0 Totals:</span>
                <Switch
                  checked={hideZeroTotals}
                  onChange={setHideZeroTotals}
                  size="small"
                />
              </Space>
              <Button
                onClick={handleExportClick}
                icon={
                  <ImageWithBasePath
                    src="assets/img/icons/xls.svg"
                    alt="Excel"
                    style={{ width: 16, height: 16 }}
                  />
                }
                title="Export Aging Report to Excel"
                disabled={brandIds.length === 0}
              >
                Export
              </Button>
            </Space>
          </div>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {!error && (
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Space>
                  <strong>Summary:</strong>
                  <span>Total Vendors: {filteredVendors.length}</span>
                  {hideZeroTotals && (
                    <span style={{ color: "#666", fontSize: "12px" }}>
                      (Filtered from {agingData.vendors?.length || 0} total)
                    </span>
                  )}
                  <span style={{ marginLeft: 16 }}>
                    Grand Total:{" "}
                    <strong
                      style={{
                        color:
                          filteredGrandTotal < 0
                            ? "green"
                            : filteredGrandTotal > 0
                            ? "red"
                            : "inherit",
                      }}
                    >
                      {isConverted ? (
                        <>
                          {displayCurrency} {formatNumberWithCommas(filteredGrandTotal * exchangeRate)}
                          <Tooltip title={`Original: $${formatNumberWithCommas(filteredGrandTotal)} (Rate: 1 USD = ${exchangeRate} ${displayCurrency})`}>
                            <SwapOutlined style={{ marginLeft: 4, fontSize: 12, color: '#1890ff' }} />
                          </Tooltip>
                        </>
                      ) : (
                        `$${formatNumberWithCommas(filteredGrandTotal)}`
                      )}
                    </strong>
                  </span>
                </Space>
                <Space>
                  <strong>Reference Date (Aging calculated from):</strong>
                  <DatePicker
                    value={referenceDate}
                    onChange={(date) => {
                      setReferenceDate(date || dayjs()); // Default to today if cleared
                    }}
                    format="MMM DD, YYYY"
                    allowClear={false}
                  />
                  <span style={{ color: "#666", fontSize: "12px" }}>
                    (Aging buckets calculated relative to this date)
                  </span>
                </Space>
              </Space>
            </div>

            <div
              className="ag-theme-alpine"
              style={{ height: 600, width: "100%" }}
            >
              <AgGridWithColumnState
                key={`vendor-aging-report-${currencyRefreshKey}`}
                tableId="vendor-aging-report"
                gridApiRef={gridApiRef}
                rowData={filteredVendors}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pinnedBottomRowData={pinnedBottomRowData}
                getRowStyle={(params) => {
                  if (params.node.rowPinned) {
                    return { fontWeight: "bold", backgroundColor: "#f0f5ff" };
                  }
                  return null;
                }}
                loading={loading}
                pagination={true}
                paginationPageSize={50}
                paginationPageSizeSelector={[25, 50, 100, 200]}
                enableRangeSelection={true}
                enableCellTextSelection={true}
                suppressMovableColumns={false}
                domLayout="normal"
              />
            </div>
          </Card>
        )}

        <ExcelExportModal
          visible={exportModalVisible}
          onCancel={() => setExportModalVisible(false)}
          onExport={handleExport}
          exportType="Vendor Aging Report"
          loading={exportLoading}
        />
      </div>
    </div>
  );
};

export default VendorAgingReport;
